import { FRAMING, assessFraming, assessLectern, modelStatesAt } from './framing.js';
import { assessModelDistance } from './model-steps.js';
import { distanceRule, footprintDistance, modelFootprint, modelStanding, } from './model-distance.js';
import { shortestTurn } from './model-motion.js';
import { ringAt, ringStartAt, ringWords } from './model-rings.js';
import { MODEL_DISTANCE, MODEL_GAITS, MODEL_IDLE, MODEL_KIND_PREFIX, MODEL_LIMITS, dutchNumber, modelSlugOf, replayModels, modelForward, turnTowards, } from './models.js';
import { assessScenery } from './scenery.js';
import { seenWell, modelSight, fogVisibility, FOG_VISIBLE } from './sight.js';
import { STAGE, STAGE_ADR032, offerStage } from './stage.js';
import { offerWorlds } from './world-catalogue.js';
import { assessStage } from './worlds.js';
/**
 * Where a library model can stand (generator v6, ADR-053): the platform works out two or three
 * named spots per model for the chosen world, and a language model picks one, rather than doing the
 * geometry itself. A spot keeps the distance rule (big: outside the apron and 6 m from the eyes;
 * small or a static exhibit: on the stage, 1.5 m from the eyes) with the box every named clip
 * sweeps, can be framed ("te groot om te overzien") and leaves the lectern room, as the very rules
 * judge it on a one-step lesson; it is not behind the world's scenery nor in it (ADR-061), nor lost in its fog
 * (sight.ts), keeps out of the views a world keeps clear, and keeps apart from the other models'
 * spots. A long model stands side-on; "kop naar je toe" is offered only as the last spot, at 12 m or
 * more. A model with a gait also gets lanes: walks between its spots that keep the rule all the way.
 */
export const MODEL_SPOTS = {
    /** Azimuths tried, degrees either side of straight ahead: the pupil's comfortable field. */
    azimuthMax: 60,
    azimuthStep: 5,
    radiusStep: 0.5,
    /** A spot's place is at most this far, metres: the sight profile's last ring. */
    radiusMax: 30,
    /** Small models and static exhibits stand on the stage, from here out. */
    stageFrom: 1.5,
    /** Spots offered per model. */
    count: 3,
    /** Metres between this model's footprint and another model's spots. */
    apart: 1,
    /** A second spot looks another way: at least this many degrees from the first. */
    turnApart: 15,
    /** "Kop naar je toe" only this far out or more, metres. */
    headOnFrom: 12,
    /** A lane is at least this long, metres. */
    laneMin: 6,
    /**
     * Code prompt v8: a gait too slow for `laneMin` in one walk (`MODEL_LIMITS.walkSeconds`) -- the
     * model kit's marsrover drives at 0.5 m/s, about 5 m in 12 s -- gets lanes from this long instead.
     */
    laneMinSlow: 3,
    /**
     * ADR-083, a built Les maken lesson: a model offered no route may walk this far at most between two of its own
     * spots (`spareLane`), or a small one from a spot of its own to a new spot (`spareWalk`), metres -- a short walk,
     * not a crossing of the view.
     */
    spareLaneMax: 9,
    /** Code prompt v8: how much farther out than its first spot a slow gait's lane may start, metres. */
    slowStartOut: 3,
    /** Long, and so shown side-on: length at least this many times the height. */
    longFactor: 1.5,
    /** Places checked by the full rules per azimuth before it is given up. */
    triesPerAzimuth: 4,
    /**
     * Code prompt v8, the close-up ("dichtbij", ADR-063 (8)): the nearest point of its body at most this
     * far from the stage centre, metres. Its footprint keeps the 6 m from the eyes all the same.
     */
    closeMax: 8,
    /** A close-up spot lies at most this many degrees either side of straight ahead. */
    closeAzimuth: 30,
    /** And is turned three-quarter: this many degrees off head-on. */
    closeTurn: 45,
    /**
     * Only when no azimuth has a three-quarter close-up: these turns off head-on, in order, nearer
     * side-on (the sea floor's fog hides the start of a three-quarter run).
     */
    closeTurnsAfter: [60, 75, 90],
    /** A close-up turned this many degrees off head-on or more reads "zijwaarts". */
    closeSideOn: 75,
    /** The run to it starts at least this far back along the way it faces, metres. */
    runMin: 10,
    /**
     * ADR-080: how far off its close-up turn a model may turn to walk back to a far spot, degrees, tried in this
     * order either way: away from the pupil first, so its turn never swings its body through the 6 m.
     */
    backTurns: [90, 105, 120, 135, 75, 150, 60, 45],
    /**
     * Code prompt v8, a still object (ADR-079 (3)): in a lesson with any other model it stands at least
     * this many degrees either side of straight ahead (and within `azimuthMax`), so the front stays free
     * for the others: a moving model's spots, its close-up and the run to it, a small model's stage.
     */
    besideFrom: 50,
};
const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];
const round1 = (n) => Math.round(n * 10) / 10 + 0;
const normalise = (degrees) => {
    const d = ((((degrees + 180) % 360) + 360) % 360) - 180;
    return d === -180 ? 180 : d + 0;
};
/** The model's named clips: every animation the library gives it. */
const namedClips = (facts) => Object.keys(facts.animations);
/** The gaits it has, slowest first (a lane walks at the slowest). */
function gaitsOf(facts) {
    return MODEL_GAITS.filter((gait) => facts.animations[gait]?.speed !== undefined).sort((a, b) => facts.animations[a].speed - facts.animations[b].speed);
}
/** Whether it is shown side-on: length at least 1.5 × its height. */
export function isLongModel(facts) {
    return facts.box.length >= MODEL_SPOTS.longFactor * facts.box.height - 1e-9;
}
/**
 * The turn that shows it as `facing` from the stage centre, standing at `azimuth`: side-on with its
 * head towards the middle of the view, or towards the pupil.
 */
export function spotTurn(azimuth, facing) {
    if (facing === 'zijwaarts')
        return normalise(azimuth >= 0 ? 90 - azimuth : -90 - azimuth);
    if (facing === 'van je af')
        return normalise(-azimuth);
    return normalise(180 - azimuth);
}
/** Azimuths from straight ahead outwards, left before right at each step. */
function azimuthOrder() {
    const order = [0];
    for (let a = MODEL_SPOTS.azimuthStep; a <= MODEL_SPOTS.azimuthMax; a += MODEL_SPOTS.azimuthStep) {
        order.push(-a, a);
    }
    return order;
}
/** Whether `azimuth` lies in a view the world keeps clear. */
function keptClear(world, azimuth) {
    return (world?.keepClear ?? []).some((sector) => Math.abs(normalise(azimuth - sector.azimuth)) <= sector.width / 2);
}
/** Whether segments ab and cd cross. */
function crosses(a, b, c, d) {
    const side = (p, q, r) => Math.sign((q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]));
    return side(a, b, c) * side(a, b, d) < 0 && side(c, d, a) * side(c, d, b) < 0;
}
/** The nearest two footprints come, horizontally; 0 when they overlap or cross. */
export function footprintGap(a, b) {
    for (let i = 0; i < a.length; i++) {
        for (let j = 0; j < b.length; j++) {
            if (crosses(a[i], a[(i + 1) % a.length], b[j], b[(j + 1) % b.length]))
                return 0;
        }
    }
    let gap = Infinity;
    for (const p of a)
        gap = Math.min(gap, footprintDistance(b, p));
    for (const p of b)
        gap = Math.min(gap, footprintDistance(a, p));
    return gap;
}
const SLOT = 'model';
/** The scene a one-step test lesson is set in: the world, or the studio without one. */
const sceneOf = (world) => world ? { type: 'world', world: world.slug } : { type: 'studio' };
/** A lesson with the model at a place, framed, then playing each of its other clips in turn. */
function placedLesson(facts, pos, turn, world, clips = namedClips(facts)) {
    const plays = clips.filter((clip) => clip !== 'staan');
    return {
        schemaVersion: 1,
        id: 'plek',
        title: 'Plek',
        language: 'nl',
        mode: 'vr',
        audience: { ageMin: 10 },
        learningGoals: ['Plek'],
        scene: sceneOf(world),
        objects: {
            [SLOT]: {
                kind: `${MODEL_KIND_PREFIX}${facts.slug}`,
                label: 'Model',
                pos: [pos[0], 0, pos[1]],
                turn,
            },
        },
        steps: [
            { id: 'zien', title: 'Zien', view: { look: SLOT } },
            ...plays.map((clip) => ({
                id: `speel-${clip}`,
                title: clip,
                models: [{ object: SLOT, play: clip }],
            })),
        ],
    };
}
/** Whether the shared rules accept a lesson with this model: distance, framing, lectern, stage. */
function rulesAccept(lesson, catalogue, world, 
/** A close-up or inspection spot (ADR-063 (8), ADR-079): the model may be too big to take in, nothing else. */
overflow = false) {
    const worlds = offerWorlds(world);
    if (assessModelDistance(lesson, catalogue, worlds).length > 0)
        return false;
    const framing = assessFraming(lesson, undefined, worlds ? { worlds } : {}, catalogue).problems;
    if (framing.some((problem) => !overflow || !isOverflow(problem)))
        return false;
    if (assessLectern(lesson, catalogue, worlds).length > 0)
        return false;
    if (assessScenery(lesson, catalogue, worlds).length > 0)
        return false;
    return assessStage(lesson, catalogue, worlds).length === 0;
}
/** The facing a model turned `turn` at `pos` shows the stage centre. */
function facingAt(pos, turn, headOn) {
    const [fx, fz] = [-Math.sin((turn * Math.PI) / 180), -Math.cos((turn * Math.PI) / 180)];
    const length = Math.hypot(pos[0], pos[1]) || 1;
    // The cosine between where it faces and the way to the pupil.
    const towards = (-pos[0] * fx - pos[1] * fz) / length;
    return Math.abs(towards) <= Math.SQRT1_2 ? 'zijwaarts' : headOn;
}
/** How wide a footprint looks from the stage centre, degrees: framing's "te groot" is wider than 70. */
function seenAcross(footprint) {
    const angles = footprint.map(([x, z]) => (Math.atan2(x, -z) * 180) / Math.PI);
    return Math.max(...angles) - Math.min(...angles);
}
/** A place, if the model may stand there turned `turn`: every rule, the sight lines, the others. */
function tryPlace(facts, world, taken, pos, turn, facing, 
/** A close-up spot (ADR-063 (8)): framing's "te groot om te overzien" is allowed. */
overflow = false, 
/** The clips it must be able to play there (default every one it has). */
clips = namedClips(facts)) {
    const standing = modelStanding(facts);
    const stage = world?.stage ?? STAGE;
    const azimuth = Math.round((Math.atan2(pos[0], -pos[1]) * 180) / Math.PI);
    if (Math.abs(azimuth) > MODEL_SPOTS.azimuthMax || keptClear(world, azimuth))
        return { checked: false };
    if (fogVisibility(world?.fog, Math.hypot(pos[0], pos[1])) < FOG_VISIBLE - 1e-9)
        return { checked: false };
    const footprint = modelFootprint(facts, pos, turn, { clips: [...clips] });
    const centre = footprintDistance(footprint, [0, 0]);
    if (standing === 'big' ? centre < stage.clear - 1e-6 : centre < MODEL_SPOTS.stageFrom - 1e-6)
        return { checked: false };
    if (taken.some((other) => footprintGap(footprint, other) < MODEL_SPOTS.apart - 1e-6))
        return { checked: false };
    // Framing would find it too big to take in: not worth the rules (a long exhibit on the stage).
    if (standing !== 'big' && !overflow && seenAcross(footprint) > FRAMING.tooBig)
        return { checked: false };
    if (!seenWell(modelSight(world, facts, pos, turn, { clips: [...clips] })))
        return { checked: false };
    if (!rulesAccept(placedLesson(facts, pos, turn, world, clips), { [facts.slug]: facts }, world, overflow))
        return { checked: true, centre };
    return {
        checked: true,
        candidate: {
            pos: [pos[0], 0, pos[1]],
            turn,
            azimuth,
            distance: round1(Math.hypot(pos[0], pos[1])),
            facing,
            footprint,
        },
    };
}
/**
 * The nearest place at each azimuth, straight ahead first, where the model stands as `facing` and
 * every rule holds, no nearer than `from` metres; lazily, so a caller stops when it has enough.
 * `beside` (ADR-079 (3)): only from `besideFrom` degrees either side, outermost first. `overflow` (ADR-079 (1)): framing's
 * "te groot om te overzien" is allowed, as on an inspection spot. `within`: no farther out than its
 * `metres`, read at every place tried, so a caller after the nearest of all lowers it as it goes.
 */
function* candidates(facts, world, taken, facing, from = 0, { beside = false, overflow = false, within, } = {}) {
    const standing = modelStanding(facts);
    const stage = world?.stage ?? STAGE;
    const nearest = standing === 'big' ? stage.clear : MODEL_SPOTS.stageFrom;
    const farthest = standing === 'small' ? stage.radius : MODEL_SPOTS.radiusMax;
    // Beside the pupil (ADR-079 (3)), the outermost first: the farther aside, the freer the front.
    const order = beside
        ? azimuthOrder()
            .filter((azimuth) => Math.abs(azimuth) >= MODEL_SPOTS.besideFrom - 1e-9)
            .reverse()
            .map((azimuth) => -azimuth)
        : azimuthOrder();
    for (const azimuth of order) {
        if (keptClear(world, azimuth))
            continue;
        const radians = (azimuth * Math.PI) / 180;
        const turn = Math.round(spotTurn(azimuth, facing));
        let tries = 0;
        for (let r = Math.max(from, nearest); r <= farthest + 1e-9; r += MODEL_SPOTS.radiusStep) {
            if (within !== undefined && r > within.metres + 1e-9)
                break;
            const pos = [round1(r * Math.sin(radians)), round1(-r * Math.cos(radians))];
            const placed = tryPlace(facts, world, taken, pos, turn, facing, overflow);
            if (placed.candidate) {
                yield placed.candidate;
                break;
            }
            // The places ADR-061 opened to a big model (its footprint nearer than ADR-032's 8.5 m) are all
            // tried: a long one is too wide to take in there, and four tries would give up before it fits.
            // From there the tries count as before, so ADR-032's stage finds the spots it always found.
            const opened = standing === 'big' && (placed.centre ?? Infinity) < STAGE_ADR032.clear - 1e-6;
            if (placed.checked && !opened && !overflow && ++tries >= MODEL_SPOTS.triesPerAzimuth)
                break;
        }
    }
}
/**
 * Whether walking from `from` to `to` in `gait` keeps the distance rule all the way, and its 12 s, in the open.
 * `face`: it turns to the pupil where it arrives (ADR-080's walk back), and that holds too.
 */
function laneHolds(facts, from, to, gait, world, face = false) {
    const walk = {
        object: SLOT,
        walkTo: [to[0], to[1]],
        gait,
        ...(face ? { face: 'pupil' } : {}),
    };
    const lesson = {
        ...placedLesson(facts, [from.pos[0], from.pos[2]], from.turn, world),
        steps: [{ id: 'lopen', title: 'Lopen', models: [walk] }],
    };
    const catalogue = { [facts.slug]: facts };
    const worlds = offerWorlds(world);
    return (assessModelDistance(lesson, catalogue, worlds).length === 0 &&
        // The whole walk in the open (ADR-061).
        (world?.scenery === undefined || assessScenery(lesson, catalogue, worlds).length === 0));
}
/** How far one walk in `gait` may go, metres: 90 % of what `MODEL_LIMITS.walkSeconds` covers. */
const laneReach = (facts, gait) => Math.min(MODEL_SPOTS.radiusMax, facts.animations[gait].speed * MODEL_LIMITS.walkSeconds * 0.9);
/** A gait too slow to cover `laneMin` in one walk (code prompt v8: `laneMinSlow`). */
const slowGait = (facts, gait) => laneReach(facts, gait) < MODEL_SPOTS.laneMin;
/**
 * A lane from `first`: straight on the way it faces, so it walks "langs" without turning, to the
 * first place at least `laneMin` on where it may stand and the whole walk keeps the rule.
 *
 * `slow` (code prompt v8): a gait too slow to cover `laneMin` in one walk gets a lane from
 * `laneMinSlow`; and where straight on keeps none (a model that faces the pupil drives at them), one
 * across the view after a quarter turn on the spot, either way, its end turned the way it drove.
 */
function laneFrom(facts, world, taken, first, gait, slow = false) {
    const most = laneReach(facts, gait);
    const short = slow && slowGait(facts, gait);
    const least = short ? MODEL_SPOTS.laneMinSlow : MODEL_SPOTS.laneMin;
    const headings = short ? [first.turn, first.turn + 90, first.turn - 90] : [first.turn];
    for (const heading of headings) {
        const turned = normalise(heading);
        const radians = (turned * Math.PI) / 180;
        const [fx, fz] = [-Math.sin(radians), -Math.cos(radians)];
        for (let t = least; t <= most + 1e-9; t += MODEL_SPOTS.radiusStep) {
            const pos = [round1(first.pos[0] + fx * t), round1(first.pos[2] + fz * t)];
            const facing = facingAt(pos, turned, 'naar je toe');
            const placed = tryPlace(facts, world, taken, pos, turned, facing);
            // The walk's time (laneHolds) counts the turn on the spot too.
            if (placed.candidate && laneHolds(facts, first, pos, gait, world))
                return placed.candidate;
        }
    }
    return undefined;
}
/** From the stage centre to the nearest point of the model's body (its box, as it stands), metres. */
export function bodyDistance(facts, pos, turn) {
    return footprintDistance(modelFootprint(facts, pos, turn), [0, 0]) + MODEL_DISTANCE.margin;
}
/**
 * At a close-up place, what the model may play besides `staan` and its gait: each once-only clip and
 * loop that keeps every rule there on its own. Undefined when it has such clips and none holds -- a
 * close-up where it can only stand is no moment.
 */
function closePlays(facts, world, taken, pos, turn, facing, base) {
    const others = namedClips(facts).filter((clip) => !base.includes(clip) && !isGaitClip(clip));
    const plays = others.filter((clip) => tryPlace(facts, world, taken, pos, turn, facing, true, [...base, clip]).candidate !==
        undefined);
    return others.length > 0 && plays.length === 0 ? undefined : plays;
}
const isGaitClip = (clip) => MODEL_GAITS.includes(clip);
/**
 * Whether `pos` lies within `SPOT_TOLERANCE.metres` of one of `places`, so a lesson writing one spot's
 * place stands on the other too (fix/closeup-spot-clash: the sea-floor shark's side-on close-up E fell
 * on its spot A, and its run D → E read as D → A). The library T. rex's close-up, 0.5 m from its A,
 * keeps its place.
 */
const clashes = (places, pos) => places.some((place) => Math.hypot(place[0] - pos[0], place[1] - pos[1]) <= SPOT_TOLERANCE.metres + 1e-9);
/**
 * Where the run to a close-up spot starts: straight back along the way it faces there, at least
 * `runMin` on, the first place where it may stand by every rule and the whole run keeps the
 * distance rule in the open -- so it arrives turned as the spot is.
 */
function runStart(facts, world, taken, close, gait, own = []) {
    const radians = (close.turn * Math.PI) / 180;
    const [fx, fz] = [-Math.sin(radians), -Math.cos(radians)];
    const most = laneReach(facts, gait);
    for (let t = MODEL_SPOTS.runMin; t <= most + 1e-9; t += MODEL_SPOTS.radiusStep) {
        const pos = [round1(close.pos[0] - fx * t), round1(close.pos[2] - fz * t)];
        if (Math.hypot(pos[0], pos[1]) > MODEL_SPOTS.radiusMax + 1e-9)
            break;
        if (clashes(own, pos))
            continue;
        const placed = tryPlace(facts, world, taken, pos, close.turn, facingAt(pos, close.turn, 'naar je toe'));
        if (!placed.candidate)
            continue;
        // From the start's own place, so the run's line is the lane's as offered.
        if (laneHolds(facts, placed.candidate, [close.pos[0], close.pos[2]], gait, world))
            return placed.candidate;
    }
    return undefined;
}
/**
 * Code prompt v8 (ADR-063 (8); Pieter, 14 Oct 2026: "I would like to see the T. rex closer to me"): a
 * close-up spot for a big model with a gait -- in front (within `closeAzimuth`), turned three-quarter
 * towards the pupil, the nearest point of its body at most `closeMax` from the stage centre -- where
 * every rule holds but framing's "te groot om te overzien": it may fill and overflow the view. Its
 * footprint keeps the 6 m from the eyes like any other spot. Offered only with the run to it, in its
 * fastest gait from `runStart`. Where no azimuth has one turned three-quarter, it is turned further
 * off head-on (`closeTurnsAfter`), nearer side-on, so the run to it starts nearer, out of the fog.
 */
function closeUpOf(facts, world, taken, 
/** This model's spots chosen before it: the close-up and its run's start take another place. */
own = []) {
    if (modelStanding(facts) !== 'big')
        return undefined;
    const gait = gaitsOf(facts).at(-1);
    if (gait === undefined)
        return undefined;
    // Too slow for a run of `runMin` in one walk (the marsrover): runStart finds none anywhere, so the
    // search is not made (it took the marsrover over 4 s).
    if (laneReach(facts, gait) < MODEL_SPOTS.runMin)
        return undefined;
    // Three-quarter wherever it can be; only then further off head-on.
    return (closeUpTurned(facts, world, taken, gait, [MODEL_SPOTS.closeTurn], own) ??
        closeUpTurned(facts, world, taken, gait, MODEL_SPOTS.closeTurnsAfter, own));
}
/** How a close-up turned `off` degrees from head-on reads. */
const closeFacing = (off) => off >= MODEL_SPOTS.closeSideOn - 1e-9 ? 'zijwaarts' : 'schuin naar je toe';
/** The first close-up over the azimuths, at each turned each of `offsets` off head-on, either way. */
function closeUpTurned(facts, world, taken, gait, offsets, own = []) {
    const stage = world?.stage ?? STAGE;
    for (const azimuth of azimuthOrder()) {
        if (Math.abs(azimuth) > MODEL_SPOTS.closeAzimuth)
            break;
        if (keptClear(world, azimuth))
            continue;
        const radians = (azimuth * Math.PI) / 180;
        for (const off of offsets) {
            const facing = closeFacing(off);
            for (const side of [1, -1]) {
                const turn = Math.round(spotTurn(azimuth, 'naar je toe') + side * off);
                const turned = normalise(turn);
                for (let r = stage.clear; r <= MODEL_SPOTS.radiusMax + 1e-9; r += MODEL_SPOTS.radiusStep) {
                    const pos = [round1(r * Math.sin(radians)), round1(-r * Math.cos(radians))];
                    if (bodyDistance(facts, pos, turned) > MODEL_SPOTS.closeMax + 1e-9)
                        break;
                    // On (or within reach of) a spot already offered, a lesson's place could be either.
                    if (clashes(own, pos))
                        continue;
                    // Only what it does there must keep 6 m: it arrives in its gait and stands, then plays.
                    const base = namedClips(facts).filter((clip) => clip === MODEL_IDLE || clip === gait);
                    const placed = tryPlace(facts, world, taken, pos, turned, facing, true, base);
                    if (!placed.candidate)
                        continue;
                    const plays = closePlays(facts, world, taken, pos, turned, facing, base);
                    if (plays === undefined)
                        continue;
                    const start = runStart(facts, world, taken, placed.candidate, gait, own);
                    if (start)
                        return { start, close: { ...placed.candidate, plays }, gait };
                    // The nearest place this way has no run to it: a farther one would have none either.
                    break;
                }
            }
        }
    }
    return undefined;
}
/**
 * Whether a model is a still object (ADR-079): one that never moves -- the library gives it no
 * animations at all, so no gait and no clip that sweeps (`modelStanding` 'exhibit': a skeleton, a
 * statue). A small one stands on the stage and fits in view anyway, so it is not one.
 */
export const isStillObject = (facts) => modelStanding(facts) === 'exhibit';
/**
 * Code prompt v8, a still object's two spots (ADR-079 (1)): first its inspection spot, the nearest
 * place every rule but framing's "te groot om te overzien" allows (the distance rule, the lectern, the
 * scenery, the sight lines, the other models), where the pupil looks round it at its parts; then the
 * nearest where framing takes it in whole as well. Undefined when the two fall together -- it fits in
 * view from its nearest place -- or either is missing: then it gets the spots any model gets.
 */
function stillSpotsOf(facts, world, taken, beside) {
    const facing = isLongModel(facts) ? 'zijwaarts' : 'naar je toe';
    // The nearest over every azimuth (the first in search order on a tie), tried all the way out at each but
    // never past the nearest found so far.
    let near;
    const within = { metres: MODEL_SPOTS.radiusMax };
    for (const candidate of candidates(facts, world, taken, facing, 0, {
        beside,
        overflow: true,
        within,
    })) {
        if (near === undefined || candidate.distance < near.distance - 1e-9)
            near = candidate;
        within.metres = near.distance - MODEL_SPOTS.radiusStep;
    }
    const whole = candidates(facts, world, taken, facing, 0, { beside }).next().value;
    if (near === undefined || whole === undefined || near.distance >= whole.distance - 1e-9)
        return undefined;
    if (clashes([[near.pos[0], near.pos[2]]], [whole.pos[0], whole.pos[2]]))
        return undefined;
    return { spots: [{ ...near, inspect: true }, whole], lanes: [] };
}
/** One model's spots and lanes, keeping apart from `taken` (the other models' spots). */
function spotsOf(facts, world, taken, closeUp = false, 
/** ADR-079 (3): a still object beside the pupil, the front left to the lesson's other models. */
beside = false) {
    // Code prompt v8 (ADR-079): a still object gets an inspection spot and one where it fits in view.
    const still = closeUp && isStillObject(facts) ? stillSpotsOf(facts, world, taken, beside) : undefined;
    if (still !== undefined)
        return still;
    const long = isLongModel(facts);
    const facing = long ? 'zijwaarts' : 'naar je toe';
    // ADR-079 (3): a still object that gets the usual spots stays beside the pupil all the same.
    const found = candidates(facts, world, taken, facing, 0, { beside });
    let first = found.next().value;
    if (first === undefined)
        return { spots: [], lanes: [] };
    const lanes = [];
    const gait = gaitsOf(facts)[0];
    let end = gait === undefined ? undefined : laneFrom(facts, world, taken, first, gait, closeUp);
    // v8, a slow gait (laneFrom): no lane from the first spot, so the lane's start a little farther out
    // the same way -- the marsrover's first spot kept the 6 m by a centimetre, and no turn could.
    if (gait !== undefined && end === undefined && closeUp && slowGait(facts, gait)) {
        const out = Math.hypot(first.pos[0], first.pos[2]);
        for (let r = out + MODEL_SPOTS.radiusStep; r <= out + MODEL_SPOTS.slowStartOut + 1e-9; r += MODEL_SPOTS.radiusStep) {
            const start = candidates(facts, world, taken, facing, r).next().value;
            const there = start && laneFrom(facts, world, taken, start, gait, true);
            if (start && there) {
                [first, end] = [start, there];
                break;
            }
        }
    }
    const chosen = [first];
    if (gait !== undefined && end !== undefined) {
        chosen.push(end);
        lanes.push({ from: 'A', to: 'B', gait, metres: round1(between(first, end)) });
    }
    // Without a head-on spot to come, a third; each looks another way than those chosen.
    const wanted = long ? MODEL_SPOTS.count - 1 : MODEL_SPOTS.count;
    for (const candidate of found) {
        if (chosen.length >= wanted)
            break;
        const apart = chosen.every((s) => Math.abs(shortestTurn(s.azimuth, candidate.azimuth)) >= MODEL_SPOTS.turnApart - 1e-9);
        if (apart)
            chosen.push(candidate);
    }
    if (long) {
        const headOn = candidates(facts, world, taken, 'kop naar je toe', MODEL_SPOTS.headOnFrom, {
            beside,
        }).next().value;
        if (headOn)
            chosen.push(headOn);
    }
    // Code prompt v8 (ADR-063 (8)): the close-up last, after the start of the run to it.
    const own = chosen.map((spot) => [spot.pos[0], spot.pos[2]]);
    const close = closeUp ? closeUpOf(facts, world, taken, own) : undefined;
    if (close !== undefined) {
        const from = LETTERS[chosen.length];
        const to = LETTERS[chosen.length + 1];
        chosen.push(close.start, close.close);
        lanes.push({ from, to, gait: close.gait, metres: round1(between(close.start, close.close)) });
        return { spots: chosen, lanes, close: close.close, run: close };
    }
    return { spots: chosen, lanes };
}
const placeOf = (spot) => [spot.pos[0], spot.pos[2]];
/**
 * ADR-080, the walk back from a close-up (`close`): in the run's gait, turning to the pupil where it arrives.
 * Straight back to the run's start when that keeps every rule; a long model turning round on its close-up
 * spot sweeps through the 6 m (the T. rex: its tail comes within 4.3 m), so else to the first far spot it
 * reaches turning away from the pupil (off its close-up turn by 45° to 150°, either way), that keeps every
 * rule as it arrives, turned to the pupil, with a walk from there to the run's start (`again`, its metres)
 * that does too and leaves it turned so the run holds once more. Undefined when it finds none: the model
 * then stays on its close-up spot, as before ADR-080.
 */
function walkBackOf(facts, world, taken, close, own) {
    const start = placeOf(close.start);
    if (laneHolds(facts, close.close, start, close.gait, world, true)) {
        return { metres: round1(between(close.start, close.close)) };
    }
    const most = laneReach(facts, close.gait);
    for (const off of MODEL_SPOTS.backTurns) {
        for (const side of [1, -1]) {
            const heading = normalise(close.close.turn + side * off);
            const [fx, fz] = modelForward(heading);
            for (let t = MODEL_SPOTS.laneMin; t <= most + 1e-9; t += MODEL_SPOTS.radiusStep) {
                const pos = [
                    round1(close.close.pos[0] + fx * t),
                    round1(close.close.pos[2] + fz * t),
                ];
                if (Math.hypot(pos[0], pos[1]) > MODEL_SPOTS.radiusMax + 1e-9)
                    break;
                if (clashes(own, pos))
                    continue;
                const turn = Math.round(turnTowards(pos, [0, 0]));
                const facing = facingAt(pos, turn, isLongModel(facts) ? 'kop naar je toe' : 'naar je toe');
                const placed = tryPlace(facts, world, taken, pos, turn, facing);
                if (!placed.candidate)
                    continue;
                if (!laneHolds(facts, close.close, pos, close.gait, world, true))
                    continue;
                // From there to the run's start, and the run again from how it arrives.
                if (!laneHolds(facts, placed.candidate, start, close.gait, world))
                    continue;
                const arrives = { ...close.start, turn: turnTowards(pos, start) };
                const again = laneHolds(facts, arrives, placeOf(close.close), close.gait, world);
                return {
                    spot: placed.candidate,
                    metres: round1(between(placed.candidate, close.close)),
                    ...(again ? { again: round1(between(placed.candidate, close.start)) } : {}),
                };
            }
        }
    }
    return undefined;
}
const between = (a, b) => Math.hypot(a.pos[0] - b.pos[0], a.pos[2] - b.pos[2]);
/**
 * ADR-083 (PO, 30 Oct 2026), a built Les maken lesson only: a model with a gait that was offered no route at
 * all -- straight on from its first spot breaks a rule, and it has no close-up run -- may still swim or walk a
 * short way between two of its own spots: the first pair (from A first) at least `laneMinSlow` and at most
 * `spareLaneMax` and one walk (`laneReach`) apart where the whole walk keeps the distance rule, its 12 s and the open
 * ground (`laneHolds`), and its turn on the spot and its walk keep `apart` from every other model's spots (`others`,
 * their footprints): it never walks through another model. Undefined when none does: the model then stands where
 * it stands.
 */
export function spareLane(facts, world, spots, others = []) {
    const gait = gaitsOf(facts)[0];
    if (gait === undefined)
        return undefined;
    for (const from of spots)
        for (const to of spots) {
            if (to === from)
                continue;
            const metres = between(from, to);
            const most = Math.min(MODEL_SPOTS.spareLaneMax, laneReach(facts, gait));
            if (metres < MODEL_SPOTS.laneMinSlow - 1e-9 || metres > most + 1e-9)
                continue;
            if (!clearOf(facts, from, to, gait, others))
                continue;
            if (laneHolds(facts, from, [to.pos[0], to.pos[2]], gait, world)) {
                return { from: from.letter, to: to.letter, gait, metres: round1(metres) };
            }
        }
    return undefined;
}
/**
 * ADR-083 (31 Oct 2026), a small model whose own spots give it no short route (`spareLane`) -- the bloodstream
 * demo's bacterium, 0.4 m long: a small model stands on the stage, and its spots there are 15° and so about
 * 1.5 m apart, short of `laneMinSlow`: the first walk from one of its own spots (from A first) to a new spot of its
 * own, straight on, else straight back, else a quarter turn either way, at least `laneMinSlow` and at most
 * `spareLaneMax` and one walk (`laneReach`) on, on the stage as its spots are, where it may stand, the whole walk
 * keeps the distance rule, its 12 s and the open ground (`laneHolds`) and it keeps `apart` from every other model's
 * spots (`others`) all the way. Undefined when none does, and for any other model (its spots lie far enough apart).
 */
function spareWalk(facts, world, spots, others) {
    const gait = gaitsOf(facts)[0];
    const letter = LETTERS[spots.length];
    if (gait === undefined || letter === undefined || modelStanding(facts) !== 'small')
        return undefined;
    const most = Math.min(MODEL_SPOTS.spareLaneMax, laneReach(facts, gait));
    const farthest = (world?.stage ?? STAGE).radius;
    for (const from of spots) {
        if (from.close === true)
            continue;
        for (const heading of [from.turn, from.turn + 180, from.turn + 90, from.turn - 90]) {
            const turned = normalise(heading);
            const radians = (turned * Math.PI) / 180;
            const [fx, fz] = [-Math.sin(radians), -Math.cos(radians)];
            for (let t = MODEL_SPOTS.laneMinSlow; t <= most + 1e-9; t += MODEL_SPOTS.radiusStep) {
                const pos = [round1(from.pos[0] + fx * t), round1(from.pos[2] + fz * t)];
                if (Math.hypot(pos[0], pos[1]) > farthest + 1e-9)
                    break;
                const facing = facingAt(pos, turned, isLongModel(facts) ? 'kop naar je toe' : 'naar je toe');
                const placed = tryPlace(facts, world, others, pos, turned, facing);
                if (placed.candidate === undefined)
                    continue;
                const to = { letter, ...placed.candidate };
                if (!clearOf(facts, from, to, gait, others))
                    continue;
                if (!laneHolds(facts, from, pos, gait, world))
                    continue;
                return {
                    spot: placed.candidate,
                    lane: { from: from.letter, to: letter, gait, metres: round1(between(from, to)) },
                    path: walkFootprints(facts, from, to, gait),
                };
            }
        }
    }
    return undefined;
}
/** Degrees between samples of a turn on the spot, and metres between samples of a walk (`clearOf`). */
const SWEEP_STEP = { degrees: 10, metres: 0.5 };
/**
 * The footprints of a model turning on `from` to face `to` and walking there, sampled every `SWEEP_STEP`.
 */
function walkFootprints(facts, from, to, gait) {
    const [x0, z0, x1, z1] = [from.pos[0], from.pos[2], to.pos[0], to.pos[2]];
    const heading = (Math.atan2(-(x1 - x0), -(z1 - z0)) * 180) / Math.PI;
    const turn = shortestTurn(from.turn, heading);
    const poses = [];
    const turns = Math.max(1, Math.ceil(Math.abs(turn) / SWEEP_STEP.degrees));
    for (let i = 0; i <= turns; i++)
        poses.push([x0, z0, from.turn + (turn * i) / turns]);
    const length = Math.hypot(x1 - x0, z1 - z0);
    const walks = Math.max(1, Math.ceil(length / SWEEP_STEP.metres));
    for (let i = 1; i <= walks; i++) {
        poses.push([x0 + ((x1 - x0) * i) / walks, z0 + ((z1 - z0) * i) / walks, heading]);
    }
    return poses.map(([x, z, t]) => modelFootprint(facts, [x, z], t, { clips: [gait] }));
}
/**
 * Whether a model turning on `from` to face `to` and walking there keeps `MODEL_SPOTS.apart` from each of
 * `others` (other models' footprints) all the way, sampled every `SWEEP_STEP`.
 */
function clearOf(facts, from, to, gait, others) {
    if (others.length === 0)
        return true;
    return walkFootprints(facts, from, to, gait).every((footprint) => others.every((other) => footprintGap(footprint, other) >= MODEL_SPOTS.apart - 1e-6));
}
/**
 * The spots and lanes for each model, in the order given (the teacher's), in `world` (absent: the
 * stage alone, nothing known of scenery or fog). Each keeps apart from the spots offered to the
 * models before it; a model left without a spot that way gets its own, wherever they fall.
 */
export function modelSpots(models, world, options = {}) {
    const taken = [];
    /** By model index: the footprints its spots added to `taken`, and its close-up run. */
    const own = new Map();
    const closeUp = options.closeUp === true;
    // Code prompt v8 (ADR-079 (3)): with any other model in the lesson, the still objects first and beside
    // the pupil, so the front stays free for the others -- a moving model's spots, close-up and run, a small
    // model's place on the stage; only as the lesson's one model may it stand straight ahead.
    const beside = closeUp && models.length > 1 && models.some(isStillObject);
    const order = models.map((facts, index) => ({ facts, index }));
    if (beside)
        order.sort((a, b) => Number(isStillObject(b.facts)) - Number(isStillObject(a.facts)));
    const placed = [];
    for (const { facts, index } of order) {
        const aside = beside && isStillObject(facts);
        let found = spotsOf(facts, world, taken, closeUp, aside);
        if (found.spots.length === 0 && taken.length > 0)
            found = spotsOf(facts, world, [], closeUp, aside);
        if (found.spots.length === 0 && aside)
            found = spotsOf(facts, world, [], closeUp);
        const clips = namedClips(facts);
        const footprints = [];
        own.set(index, { footprints, ...(found.run === undefined ? {} : { run: found.run }) });
        for (const spot of found.spots) {
            // A close-up spot keeps others off what it does there -- standing, its run, its `plays`
            // (ADR-063 (8)); any other spot, every clip.
            const there = spot === found.close
                ? clips.filter((clip) => clip === MODEL_IDLE ||
                    isGaitClip(clip) ||
                    (found.close.plays ?? []).includes(clip))
                : clips;
            const footprint = modelFootprint(facts, [spot.pos[0], spot.pos[2]], spot.turn, {
                clips: there,
            });
            taken.push(footprint);
            footprints.push(footprint);
        }
        placed[index] = {
            slug: facts.slug,
            standing: modelStanding(facts),
            spots: found.spots.map(({ footprint: _footprint, ...spot }, i) => ({
                letter: LETTERS[i],
                ...spot,
                ...(found.close !== undefined && spot.pos === found.close.pos
                    ? {
                        close: true,
                        near: round1(bodyDistance(facts, [spot.pos[0], spot.pos[2]], spot.turn)),
                        plays: found.close.plays ?? [],
                    }
                    : {}),
            })),
            lanes: found.lanes,
        };
    }
    // ADR-080: the far spots of the walks back, apart from every other model's spots and those found before.
    const extra = [];
    if (options.walkBack === true) {
        // ADR-080: the walks back, apart from every other model's spots and the far spots found before.
        for (const { facts, index } of order) {
            const run = own.get(index)?.run;
            const entry = placed[index];
            if (run === undefined)
                continue;
            const others = [...own].filter(([at]) => at !== index).flatMap(([, one]) => one.footprints);
            const points = entry.spots.map((spot) => [spot.pos[0], spot.pos[2]]);
            const back = walkBackOf(facts, world, [...others, ...extra], run, points);
            if (back === undefined)
                continue;
            const lane = entry.lanes.find((one) => one.to === entry.spots.find((s) => s.close)?.letter);
            const spots = [...entry.spots];
            const home = back.spot === undefined ? lane.from : LETTERS[spots.length];
            if (back.spot !== undefined) {
                const { footprint, ...far } = back.spot;
                spots.push({ letter: home, ...far });
                extra.push(footprint);
            }
            const lanes = [
                ...entry.lanes,
                { from: lane.to, to: home, gait: run.gait, metres: back.metres },
            ];
            if (back.again !== undefined) {
                lanes.push({ from: home, to: lane.from, gait: run.gait, metres: back.again });
            }
            placed[index] = { ...entry, spots, lanes };
        }
    }
    if (options.walkBack === true) {
        // ADR-083: a model with a gait offered no route gets a short one between its own spots, when one holds.
        for (const { facts, index } of order) {
            const entry = placed[index];
            if (entry.lanes.length > 0)
                continue;
            // M1 (review): never through another model's spots, nor the far spots of their walks back.
            const others = [
                ...[...own].filter(([at]) => at !== index).flatMap(([, one]) => one.footprints),
                ...extra,
            ];
            const lane = spareLane(facts, world, entry.spots, others);
            if (lane !== undefined) {
                placed[index] = { ...entry, lanes: [lane] };
                continue;
            }
            // A small model's spots, on the stage, too close together for that: a short walk to a spot of its own.
            const walk = spareWalk(facts, world, entry.spots, others);
            if (walk === undefined)
                continue;
            // Review (31 Oct): its new spot and the whole walk there, so a model after it keeps off both.
            const { footprint, ...end } = walk.spot;
            extra.push(footprint, ...walk.path);
            placed[index] = {
                ...entry,
                spots: [...entry.spots, { letter: walk.lane.to, ...end }],
                lanes: [walk.lane],
            };
        }
    }
    return placed;
}
/** Where a spot is, in a teacher's words: "recht vooruit", "links voor", "rechts". */
export function spotDirection(azimuth) {
    if (Math.abs(azimuth) < 7.5)
        return 'recht vooruit';
    const side = azimuth < 0 ? 'links' : 'rechts';
    return Math.abs(azimuth) <= 35 ? `${side} voor` : side;
}
/**
 * "recht vooruit, 10 m, zijwaarts"; a close-up spot "recht vooruit, dichtbij (kop op 6,9 m), schuin
 * naar je toe", or "…, zijwaarts" for one turned side-on (ADR-063 (8)); an inspection spot "rechts,
 * dichtbij om te bekijken (3,5 m), zijwaarts" (ADR-079).
 */
export function spotWords(spot) {
    const far = spot.close === true
        ? `dichtbij (kop op ${dutchNumber(spot.near ?? spot.distance)} m)`
        : spot.inspect === true
            ? `dichtbij om te bekijken (${dutchNumber(spot.distance)} m)`
            : `${dutchNumber(spot.distance)} m`;
    const facing = spot.facing === 'van je af' ? 'met zijn rug naar je toe' : spot.facing;
    return `${spotDirection(spot.azimuth)}, ${far}, ${facing}`;
}
/** The fourth side of a close look: the model turned -90° where it stands, the side `spotTurn` gives a spot on the left. */
const OTHER_SIDE_TURN = -90;
/**
 * "Eén ding van dichtbij": the one model at one place straight ahead, at the nearest distance its standing allows
 * (the same rule modelSpots holds: bodyDistance ≥ MODEL_DISTANCE's minimum for its standing, + margin, rounded to
 * 10 cm), turned so the pupil sees its front (A), its left side (B), its back (C) and its right side (D). Letters A–D.
 * A lesson turns it in place from one side to the next (a step's `turn`, Task 2b), so the place holds at every
 * heading on the way too, as the distance rule sweeps it (its footprint with the margin's square corners, from
 * where the class starts): a corner swung towards the pupil comes nearer than any side does. Framing must take it
 * in on every side as well (24 Oct), so a big exhibit stands further out than the distance rule alone puts it; no
 * spots when no place within `MODEL_SPOTS.radiusMax` holds both (the generator then refuses it as `no-close-look`).
 */
/** Every whole degree: the headings a turn in place sweeps, checked for a close look's place. */
const EVERY_HEADING = Array.from({ length: 360 }, (_, k) => k - 179);
/** Between two whole degrees a corner may come this little nearer (a 12 m model: well under 1 mm). */
const HEADING_SLACK = 0.005;
/**
 * Whether framing ("Onderwerp in beeld", docs/04's rules) takes the model in from the studio at `pos`, on
 * each side as a close look turns it there: placed on A, then turned in place to B, C, D and back to A,
 * one step each, looked at (24 Oct: the standing skeleton's 5.6 m was "te groot om te overzien").
 */
function framedAllRound(facts, pos, turns) {
    const lesson = {
        ...placedLesson(facts, pos, turns[0], undefined, []),
        steps: [...turns.slice(1), turns[0]].map((turn, i) => ({
            id: `kant-${i}`,
            title: 'Kant',
            view: { look: SLOT },
            models: [{ object: SLOT, turn }],
        })),
    };
    return assessFraming(lesson, undefined, {}, { [facts.slug]: facts }).problems.length === 0;
}
export function closeLookSpots(facts) {
    // ADR-084: offered places keep the offer's distances (`offerStage`), not the 3 m a lesson may come to.
    const minimum = distanceRule(modelStanding(facts), false, offerStage(STAGE)).eyes;
    const turns = [
        ['naar je toe', spotTurn(0, 'naar je toe')],
        ['zijwaarts', spotTurn(0, 'zijwaarts')],
        ['van je af', spotTurn(0, 'van je af')],
        ['zijwaarts', OTHER_SIDE_TURN],
    ];
    const steps = Math.round(MODEL_SPOTS.radiusMax * 10);
    for (let i = 0; i <= steps; i++) {
        const z = -(i / 10);
        const pos = [0, z];
        if (turns.every(([, turn]) => bodyDistance(facts, pos, turn) >= minimum - 1e-9) &&
            EVERY_HEADING.every((turn) => footprintDistance(modelFootprint(facts, pos, turn, { clips: [MODEL_IDLE] }), [0, 0]) >=
                minimum + HEADING_SLACK) &&
            // Framing too, or a lesson on these spots could never pass it: further out until it does.
            framedAllRound(facts, pos, turns.map(([, turn]) => turn))) {
            return turns.map(([facing, turn], n) => ({
                letter: LETTERS[n],
                pos: [0, 0, z + 0],
                turn,
                azimuth: 0,
                distance: round1(-z),
                facing,
            }));
        }
    }
    return [];
}
/** As a prompt lists it: `A recht vooruit, 10 m, zijwaarts: pos [0, 0, -10], turn 90`. */
export function spotLine(spot) {
    return `${spot.letter} ${spotWords(spot)}: pos [${spot.pos[0]}, 0, ${spot.pos[2]}], turn ${spot.turn}`;
}
/** As a prompt lists it: `A → B lopen, 7 m`. */
export function laneLine(lane) {
    return `${lane.from} → ${lane.to} ${lane.gait}, ${dutchNumber(lane.metres)} m`;
}
/** How near a place must be to a spot to stand on it: metres, and degrees for a turn. */
export const SPOT_TOLERANCE = { metres: 0.3, degrees: 10 };
/**
 * Every spot `pos` ([x, z]) stands on, turned `turn` when given: nearest first, then the nearest turn
 * (two spots may share a place turned apart, as fix/closeup-side-on offered the sea-floor shark).
 */
export function spotsAt(spots, pos, turn) {
    const off = (spot) => Math.hypot(spot.pos[0] - pos[0], spot.pos[2] - pos[1]);
    const turned = (spot) => turn === undefined ? 0 : Math.abs(shortestTurn(spot.turn, turn));
    return spots
        .filter((spot) => off(spot) <= SPOT_TOLERANCE.metres + 1e-9 && turned(spot) <= SPOT_TOLERANCE.degrees + 1e-9)
        .sort((a, b) => off(a) - off(b) || turned(a) - turned(b));
}
/** The spot `pos` ([x, z]) stands on, turned `turn` when given (`spotsAt`'s first); undefined when none. */
export function spotAt(spots, pos, turn) {
    return spotsAt(spots, pos, turn)[0];
}
/**
 * The offered lane a walk from `from` to `to` goes along, if any: every spot each end stands on is
 * tried, so a spot sharing a place with another never hides the route (fix/closeup-spot-clash).
 */
function laneAt(entry, from, to) {
    const starts = spotsAt(entry.spots, from);
    const ends = spotsAt(entry.spots, to);
    return entry.lanes.find((lane) => starts.some((spot) => spot.letter === lane.from) &&
        ends.some((spot) => spot.letter === lane.to));
}
/**
 * A model's close-up run (ADR-063 (8)) and its walk back (ADR-080): the close-up spot (`isLimitedOverflow`),
 * the run's start, the lane between them and, when offered, the lane `back` from the close-up and, when that
 * ends on a far spot of its own rather than the run's start, the lane `again` from there to the run's start.
 * Undefined for a model without a close-up.
 */
export function closeUpRun(entry) {
    const close = entry?.spots.find(isLimitedOverflow);
    const run = close === undefined ? undefined : entry.lanes.find((one) => one.to === close.letter);
    const start = run === undefined ? undefined : entry.spots.find((one) => one.letter === run.from);
    if (close === undefined || run === undefined || start === undefined)
        return undefined;
    const back = entry.lanes.find((one) => one.from === close.letter);
    const again = back === undefined || back.to === run.from
        ? undefined
        : entry.lanes.find((one) => one.from === back.to && one.to === run.from);
    return {
        close,
        start,
        run,
        ...(back === undefined ? {} : { back }),
        ...(again === undefined ? {} : { again }),
    };
}
/** At most this many steps look at a model up close on its close-up spot (ADR-063 (8)). */
export const CLOSE_UP_STEPS = 3;
/**
 * The one place that says how long a model may fill and overflow the view on `spot` (ADR-063 (8),
 * ADR-079 (2)), which check-lesson, the builder's design check and the spot search all follow: a moving
 * model on its close-up spot in at most `CLOSE_UP_STEPS` steps; a still object on its inspection spot
 * in any number -- a whole round may be about its parts; anywhere else in none.
 */
export function overflowSteps(spot) {
    if (spot.inspect === true)
        return Infinity;
    return spot.close === true ? CLOSE_UP_STEPS : 0;
}
/**
 * Whether `spot` lets its model overflow the view in a limited number of steps (ADR-063 (8)): a moving
 * model's close-up spot, never a still object's inspection spot (ADR-079 (2)). The builder's guide and
 * its stage-1 count read the limited close-up by this.
 */
export const isLimitedOverflow = (spot) => {
    const steps = overflowSteps(spot);
    return steps > 0 && Number.isFinite(steps);
};
/**
 * The framing problems such a spot excuses (ADR-063 (8), ADR-079 (2)): only "te groot om te overzien".
 * Behind the pupil, too near the eyes, on its side, and the lectern stay problems everywhere.
 */
export const isOverflow = (problem) => problem.kind === 'big';
/**
 * Code prompt v8 (ADR-063 (8), ADR-079 (2)): by step id, the model objects that stand on a spot where
 * they may overflow the view (`overflowSteps`) at the step's end -- within `SPOT_TOLERANCE.metres`, any
 * turn: a run ends turned as it ran -- each with the number of steps it may do so there.
 */
export function closeUpObjects(lesson, spots, catalogue, worlds) {
    const close = new Map();
    for (const [id, object] of Object.entries(lesson.objects ?? {})) {
        const slug = modelSlugOf(object.kind);
        const up = (spots.find((entry) => entry.slug === slug)?.spots ?? []).filter((s) => overflowSteps(s) > 0);
        if (up.length > 0)
            close.set(id, up);
    }
    const found = new Map();
    if (close.size === 0)
        return found;
    const statesAt = modelStatesAt(lesson, catalogue, undefined, worlds);
    lesson.steps.forEach((step, index) => {
        const states = statesAt(index);
        for (const [id, up] of close) {
            const pos = states.get(id)?.pos;
            const spot = pos === undefined ? undefined : spotAt(up, [pos[0], pos[2]]);
            if (spot === undefined)
                continue;
            found.set(step.id, (found.get(step.id) ?? new Map()).set(id, overflowSteps(spot)));
        }
    });
    return found;
}
/**
 * assessFraming's problems as a lesson on its offered spots is held to them (ADR-063 (8), ADR-079 (2)):
 * "te groot om te overzien" is dropped for a model on a spot where it may overflow the view
 * (`closeUpObjects`), in its first `overflowSteps` such steps; past them it is kept and marked `over`.
 * Every other problem is kept as it is, in order.
 */
export function framingOnSpots(problems, onSpots) {
    const seen = new Map();
    return problems.flatMap((problem) => {
        const limit = onSpots?.get(problem.step)?.get(problem.object);
        if (!isOverflow(problem) || limit === undefined)
            return [{ problem }];
        const count = (seen.get(problem.object) ?? 0) + 1;
        seen.set(problem.object, count);
        return count <= limit ? [] : [{ problem, over: true }];
    });
}
const placeText = (pos, turn) => `[${round1(pos[0])}, 0, ${round1(pos[1])}], ${Math.round(turn)}°`;
/**
 * "Staat op een aangeboden plek" (ADR-053): every big model of `spots` (by slug) starts on one of its
 * spots, as declared (place and turn), and walks only along its lanes. A Spannend walk to the pupil is
 * the engine's own and is not held to them; small models and static exhibits are not held at all.
 * With `rings` (ADR-054), a big model may also start on an offered ring's start, and must go round only
 * on an offered ring.
 */
export function assessOnSpots(lesson, spots, catalogue, rings = []) {
    const bySlug = new Map(spots.map((entry) => [entry.slug, entry]));
    const ringsBySlug = new Map(rings.map((entry) => [entry.slug, entry.rings]));
    const problems = [];
    const held = new Map();
    for (const [id, object] of Object.entries(lesson.objects ?? {})) {
        const slug = modelSlugOf(object.kind);
        const entry = slug === undefined ? undefined : bySlug.get(slug);
        if (!entry || entry.standing !== 'big' || !object.pos)
            continue;
        if (entry.spots.length === 0 && (ringsBySlug.get(entry.slug)?.length ?? 0) === 0)
            continue;
        held.set(id, entry);
        const pos = [object.pos[0], object.pos[2]];
        const turn = object.turn ?? 0;
        if (spotAt(entry.spots, pos, turn))
            continue;
        const ringsHere = ringsBySlug.get(entry.slug) ?? [];
        // ADR-054: a model that goes round may start where its ring starts.
        if (ringStartAt(ringsHere, pos, turn))
            continue;
        const offered = [
            ...entry.spots.map((s) => `${s.letter} (${placeText([s.pos[0], s.pos[2]], s.turn)})`),
            ...ringsHere.map((r) => `${r.code} (${placeText([r.start[0], r.start[2]], r.turn)})`),
        ];
        problems.push({
            object: id,
            kind: 'declared',
            en: `"${id}" does not stand on a spot it was offered (${placeText(pos, turn)}): use ${offered.join(', ')}`,
            nl: `"${id}" staat niet op een aangeboden plek (${placeText(pos, turn)}): kies ${offered.join(', ')}`,
        });
    }
    if (held.size === 0)
        return problems;
    const replay = replayModels(lesson, { catalogue });
    replay.steps.forEach((moves, index) => {
        for (const [id, move] of moves) {
            const entry = held.get(id);
            if (!entry)
                continue;
            if (move.circle) {
                const ringsHere = ringsBySlug.get(entry.slug) ?? [];
                if (ringAt(ringsHere, move.circle))
                    continue;
                const stepId = lesson.steps[index].id;
                const offered = ringsHere.length === 0
                    ? 'dit model krijgt geen rondjes: laat het op zijn plek een eigen animatie spelen'
                    : `rondjes: ${ringsHere.map((r) => `${r.code} ${ringWords(r)}`).join('; ')}`;
                problems.push({
                    object: id,
                    step: stepId,
                    kind: 'ring',
                    en: `step "${stepId}": "${id}" goes round no ring it was offered`,
                    nl: `stap "${stepId}": "${id}" gaat niet rond op een aangeboden rondje (${offered})`,
                });
                continue;
            }
            const walk = move.walk;
            if (!walk)
                continue;
            if (laneAt(entry, walk.from, walk.to))
                continue;
            const step = lesson.steps[index].id;
            const offered = entry.lanes.length === 0
                ? 'dit model krijgt geen routes: laat het staan en draai de leerling ernaartoe'
                : `routes: ${entry.lanes.map(laneLine).join('; ')}`;
            problems.push({
                object: id,
                step,
                kind: 'walk',
                en: `step "${step}": "${id}" walks along no lane it was offered`,
                nl: `stap "${step}": "${id}" loopt niet langs een aangeboden route (${offered})`,
            });
        }
    });
    return problems;
}
//# sourceMappingURL=model-spots.js.map